<template>
  <Loader :value="promises.fetch" class="project-flatfinder">
    <template #default>
      <div v-if="settingsKeys.length" class="project-flatfinder__actions">
        <ButtonComponent :label="$tc('adaptation', 2)" icon="fa-pro-light:sliders" @click="settingsOpen = true" />
      </div>

      <ModalComponent :show.sync="settingsOpen" :clickaway="true">
        <CardV2 variant="prompt" :title="$tc('adaptationForUnitSelector', 2)" class="project-flatfinder__settings">
          <template #default>
            <CustomFields
              v-if="settingsCustomFields"
              v-model="settingsCustomFields"
              :schema="customFieldSchemas"
              :data="data"
              :placement="['project:flatfinders-settings']"
              :validator="validator"
              validator-prefix="customFields"
              :disabledFields="$path('metadata.disabledFields.project', item)"
            />
          </template>
          <template #actions>
            <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="settingsOpen = false" />
            <ButtonComponent
              theme="primary"
              :label="$t('confirm')"
              :icon="['fal', 'arrow-right']"
              @click="submitSettings"
            />
          </template>
        </CardV2>
      </ModalComponent>

      <div class="project-flatfinder__items">
        <Card
          v-for="flatfinder in item.flatfinders"
          :value="flatfinder"
          :key="flatfinder.id"
          :to="{
            name: 'project-flatfinder-edit',
            params: { project: item.id, id: flatfinder.id },
          }"
        />

        <Card
          :value="{ name: $t('createUnitSelector') }"
          :icon="['fal', 'plus']"
          @click.native="createFlatfinder"
          style="cursor: pointer"
        />

        <UpsellBanner
          v-if="item.id && !addons.includes('project:presentation')"
          :title="$t('ctaResidentialSaleUpgradeTitle')"
          :description="$t('ctaResidentialSaleUpgradeDescription')"
          :action="$t('getStarted')"
          alignment="vertical"
          @action="showProjectPresentationUpsell"
        />
      </div>
      <div class="project-flatfinder__navigation">
        <slot name="navigation" :show-save="false" />
      </div>

      <CustomFields
        v-if="value && value.customFields"
        v-model="value.customFields"
        :schema="customFieldSchemas"
        :data="data"
        :placement="['flatfinders', 'project:flatfinders']"
        :validator="validator"
        validator-prefix="customFields"
        :disabledFields="$path('metadata.disabledFields.project', item)"
      />

      <UpsellBanner
        v-if="isNo"
        title="Trenger du visualiseringer?"
        action="Gå til bestilling"
        @action="visualizationRoute"
        class="mt-2"
      >
        <template #description>
          <span
            >Vi i Kvass kan hjelpe deg med visualisering av ditt prosjekt. For å få den beste enhetsvelgeren anbefaler
            vi at du har gode vinkler og det beste utgangspunktet. Bestill en uforpliktende prat</span
          >&nbsp;
          <a href="https://www.kvass.no/produkter/3d-visualiseringer" target="_blank">Les mer</a>
        </template>
      </UpsellBanner>
    </template>
  </Loader>
</template>

<script>
import { i18n } from '@/i18n'
import { mapState, mapActions } from 'vuex'
import { clone } from 'ramda'

import Card from '@/components/Flatfinder/Card'
import CardV2 from '@/components/Card-v2'
import UpsellBanner from '@/components/Upsell/Banner'
import CustomFields from '@kvass/custom-fields'
import { ModalComponent } from 'vue-elder-modal'

const meta = {
  order: 103,
  id: 'flatfinders',
  label: i18n.tc('unitSelector', 2),
  icon: ['fal', 'layer-plus'],
}

export { meta }
export default {
  props: {
    value: Object,
    data: Object,
    labels: Object,
    validator: Object,
    customFieldSchemas: { type: Array, default: () => [] },
  },

  data() {
    return {
      promises: {
        fetch: null,
      },
      items: [],
      settingsOpen: false,
      settingsCustomFields: null,
    }
  },
  computed: {
    ...mapState('Project', ['item']),
    addons() {
      return this.$path('item.metadata.addons') || []
    },
    isNo() {
      return this.$store.state.enums.Region.value === 'NO'
    },
    settingsKeys() {
      const placement = 'project:flatfinders-settings'
      const [resource, step] = placement.split(':')

      const matches = p => {
        if (!p) return false
        if (typeof p === 'string') return p === placement
        if (Array.isArray(p)) return p.some(matches)
        if (typeof p === 'object') return p.resource === resource && p.step === step
        return false
      }

      return (this.customFieldSchemas || []).filter(s => matches(s.placement)).map(s => s.key)
    },
  },
  watch: {
    settingsOpen(open) {
      if (!this.value) return

      this.settingsCustomFields = open ? clone(this.value.customFields || {}) : null
    },
  },
  methods: {
    ...mapActions('Project', ['fetch']),
    createFlatfinder() {
      if (!['project:presentation', 'project:flatfinder'].some(a => this.addons.includes(a)))
        return this.showProjectPresentationUpsell()

      return this.$router.push({ name: 'project-flatfinder-create', params: { project: this.item.id } })
    },
    showProjectPresentationUpsell() {
      this.$bus.emit('pricing:show', {
        addon: 'project:presentation',
        options: { units: this.$path('item.metadata.limits.units') },
        action: () => this.addon('project:presentation'),
      })
    },
    addon(addon) {
      return this.$store
        .dispatch('Project/addon', {
          addon,
          state: true,
          project: this.item.id,
        })
        .then(() => this.addons.push(addon))
        .then(() => {
          this.$router.push({ name: 'project-flatfinder-create', params: { project: this.item.id } })
        })
    },
    visualizationRoute() {
      this.$router.push({ name: 'form', query: { target: '/api/form/visualization' } })
    },
    submitSettings() {
      this.$set(this.value, 'customFields', {
        ...(this.value.customFields || {}),
        ...this.settingsCustomFields,
      })
      this.settingsOpen = false
    },
  },
  created() {
    this.promises.fetch = this.fetch()
  },
  components: {
    Card,
    CardV2,
    UpsellBanner,
    CustomFields,
    ModalComponent,
  },
}
</script>

<style lang="scss">
.project-flatfinder {
  .elder-loader__element {
    background-color: transparent;
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 1rem;
  }

  &__items {
    display: grid;
    gap: 2rem;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }

  &__item {
    position: relative;

    &-name {
      text-align: center;
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      padding: 1rem;
      background-color: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(8px);
    }

    &--create {
      display: flex;
      flex-direction: column;
      aspect-ratio: 5 / 4;

      .project-flatfinder__item {
        &-name {
          background-color: transparent;
          position: initial;
          padding-top: 0;
        }

        &-icon {
          flex: 1;
          padding-block: 2rem;
        }
      }
    }
  }

  &__navigation {
    margin-top: 2rem;
    display: flex;
    justify-content: center;
    gap: 1rem;
  }

  &__settings {
    width: 1000px;
    max-width: 90vw;
  }
}
</style>
